/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.wiz3r.database.Wiz3rDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import models.*
import studio.PaymentFrequency

/**
 * Database-backed implementation of SavingsGoalsRepository
 * Persists goals and recommendations to SQLDelight database
 */
class SavingsGoalsRepositoryImpl(
    private val database: Wiz3rDatabase,
    private val userId: String
) : SavingsGoalsRepository {

    override suspend fun getActiveGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        return withContext(Dispatchers.Default) {
            try {
                println("🔍 RepositoryImpl.getActiveGoals: userId=$userId, constructor userId=${this@SavingsGoalsRepositoryImpl.userId}")
                val goals = database.schemaQueries.selectActiveGoalsByUser(userId)
                    .awaitAsList()
                    .map { it.toSavingsGoal() }
                println("✅ RepositoryImpl.getActiveGoals: Found ${goals.size} goals")
                DatabaseResult.Success(goals)
            } catch (e: Exception) {
                println("❌ RepositoryImpl.getActiveGoals: Error - ${e.message}")
                e.printStackTrace()
                DatabaseResult.Error(e, "Failed to fetch active goals: ${e.message}")
            }
        }
    }

    override suspend fun getAllGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        return withContext(Dispatchers.Default) {
            try {
                val goals = database.schemaQueries.selectAllGoalsByUser(userId)
                    .awaitAsList()
                    .map { it.toSavingsGoal() }
                DatabaseResult.Success(goals)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch all goals: ${e.message}")
            }
        }
    }

    override suspend fun getGoalById(goalId: String): DatabaseResult<SavingsGoal?> {
        return withContext(Dispatchers.Default) {
            try {
                val goal = database.schemaQueries.selectGoalById(goalId)
                    .awaitAsOneOrNull()
                    ?.toSavingsGoal()
                DatabaseResult.Success(goal)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch goal: ${e.message}")
            }
        }
    }

    override suspend fun createGoal(goal: SavingsGoal): DatabaseResult<SavingsGoal> {
        return withContext(Dispatchers.Default) {
            try {
                // Upsert so this is idempotent: the same goal id can arrive via a
                // user-initiated create AND via a WebSocket "goal-created" replay,
                // possibly out of order. INSERT OR CONFLICT DO UPDATE makes both
                // paths safe — last write wins on conflict.
                val alreadyExisted = database.schemaQueries.selectGoalById(goal.id).awaitAsOneOrNull() != null
                println("💾 RepositoryImpl.createGoal: Saving goal '${goal.name}' with userId=$userId (existed=$alreadyExisted)")
                database.schemaQueries.upsertSavingsGoal(
                    id = goal.id,
                    user_id = userId,
                    name = goal.name,
                    description = goal.description ?: "",
                    target_amount = goal.targetAmount,
                    current_amount = goal.currentAmount,
                    deadline = goal.deadline.toString(),
                    category = goal.category.name,
                    priority = goal.priority.name,
                    created_date = goal.createdDate.toString(),
                    is_active = if (goal.isActive) 1L else 0L,
                    icon_emoji = goal.iconEmoji ?: ""
                )
                // Persist Wiz 3R AI fields in the same write. The upsert above
                // doesn't touch those columns (positional-param contract), so
                // we always follow up with the dedicated AI-fields update.
                database.schemaQueries.updateGoalAiFields(
                    account_type = goal.accountType?.name,
                    debt_interest_rate = goal.debtInterestRate,
                    debt_remaining_balance = goal.debtRemainingBalance,
                    debt_remaining_months = goal.debtRemainingMonths?.toLong(),
                    debt_payment_frequency = goal.debtPaymentFrequency?.name,
                    debt_monthly_equivalent_payment = goal.debtMonthlyEquivalentPayment,
                    id = goal.id,
                    user_id = userId,
                )
                println("✅ RepositoryImpl.createGoal: Goal '${goal.name}' saved successfully with userId=$userId")

                // Sample recommendations are a one-time seed for brand-new goals — skip
                // on upsert-over-existing so sync replays don't keep re-inserting them.
                if (!alreadyExisted) {
                    generateSampleRecommendations(goal, userId)
                }

                DatabaseResult.Success(goal)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to create goal: ${e.message}")
            }
        }
    }

    override suspend fun updateGoal(goal: SavingsGoal): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                println("💾 RepositoryImpl.updateGoal: Updating goal '${goal.name}' (${goal.id}) with userId=$userId, currentAmount=${goal.currentAmount}")
                database.schemaQueries.updateGoal(
                    name = goal.name,
                    description = goal.description ?: "",
                    target_amount = goal.targetAmount,
                    current_amount = goal.currentAmount,
                    deadline = goal.deadline.toString(),
                    category = goal.category.name,
                    priority = goal.priority.name,
                    icon_emoji = goal.iconEmoji ?: "",
                    id = goal.id,
                    user_id = userId
                )
                // Persist Wiz 3R AI fields in the same write. Without this,
                // local stays stale even after a server roundtrip refresh,
                // so the UI shows null on re-open after a save.
                database.schemaQueries.updateGoalAiFields(
                    account_type = goal.accountType?.name,
                    debt_interest_rate = goal.debtInterestRate,
                    debt_remaining_balance = goal.debtRemainingBalance,
                    debt_remaining_months = goal.debtRemainingMonths?.toLong(),
                    debt_payment_frequency = goal.debtPaymentFrequency?.name,
                    debt_monthly_equivalent_payment = goal.debtMonthlyEquivalentPayment,
                    id = goal.id,
                    user_id = userId,
                )
                println("✅ RepositoryImpl.updateGoal: Goal '${goal.name}' updated successfully with userId=$userId")
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to update goal: ${e.message}")
            }
        }
    }

    override suspend fun updateGoalProgress(goalId: String, currentAmount: Double): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.updateGoalProgress(
                    current_amount = currentAmount,
                    id = goalId
                )
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to update progress: ${e.message}")
            }
        }
    }

    override suspend fun updateGoalAiFields(
        goalId: String,
        userId: String,
        accountType: GoalAccountType?,
        debtInterestRate: Double?,
        debtRemainingBalance: Double?,
        debtRemainingMonths: Int?,
        debtPaymentFrequency: PaymentFrequency?,
        debtMonthlyEquivalentPayment: Double?,
    ): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.updateGoalAiFields(
                    account_type = accountType?.name,
                    debt_interest_rate = debtInterestRate,
                    debt_remaining_balance = debtRemainingBalance,
                    debt_remaining_months = debtRemainingMonths?.toLong(),
                    debt_payment_frequency = debtPaymentFrequency?.name,
                    debt_monthly_equivalent_payment = debtMonthlyEquivalentPayment,
                    id = goalId,
                    user_id = userId,
                )
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to update goal AI fields: ${e.message}")
            }
        }
    }

    override suspend fun markGoalInactive(goalId: String): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.markGoalInactive(goalId)
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to mark goal inactive: ${e.message}")
            }
        }
    }

    override suspend fun deleteGoal(goalId: String, userId: String): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.deleteGoal(goalId, userId)
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to delete goal: ${e.message}")
            }
        }
    }

    override suspend fun getGoalsByCategory(userId: String, category: GoalCategory): DatabaseResult<List<SavingsGoal>> {
        return withContext(Dispatchers.Default) {
            try {
                val goals = database.schemaQueries.selectGoalsByCategory(userId, category.name)
                    .awaitAsList()
                    .map { it.toSavingsGoal() }
                DatabaseResult.Success(goals)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch goals by category: ${e.message}")
            }
        }
    }

    override suspend fun getGoalsNearingDeadline(userId: String): DatabaseResult<List<SavingsGoal>> {
        return withContext(Dispatchers.Default) {
            try {
                val goals = database.schemaQueries.selectGoalsNearingDeadline(userId)
                    .awaitAsList()
                    .map { it.toSavingsGoal() }
                DatabaseResult.Success(goals)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch goals nearing deadline: ${e.message}")
            }
        }
    }

    override fun observeActiveGoals(userId: String): Flow<List<SavingsGoal>> {
        // U2: Reactive query using SQLDelight .asFlow() for real-time UI updates
        return database.schemaQueries
            .selectActiveGoalsByUser(userId)
            .asFlow()
            .mapToList(Dispatchers.Default)
            .map { dbGoals ->
                dbGoals.map { it.toSavingsGoal() }
            }
    }

    override suspend fun saveRecommendations(
        recommendations: List<SavingsRecommendation>,
        userId: String
    ): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                recommendations.forEach { rec ->
                    database.schemaQueries.insertRecommendation(
                        goal_id = rec.goalId,
                        user_id = userId,
                        spending_category = rec.spendingCategory,
                        current_monthly_spending = rec.currentMonthlySpending,
                        recommended_monthly_spending = rec.recommendedMonthlySpending,
                        suggested_reduction = rec.suggestedReduction,
                        rationale = rec.rationale,
                        potential_savings = rec.potentialSavings,
                        confidence_score = rec.confidenceScore,
                        priority = rec.priority.toLong()
                    )
                }
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to save recommendations: ${e.message}")
            }
        }
    }

    override suspend fun getRecommendationsForGoal(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return withContext(Dispatchers.Default) {
            try {
                val recommendations = database.schemaQueries.selectRecommendationsByGoal(goalId)
                    .awaitAsList()
                    .map { it.toSavingsRecommendation() }
                DatabaseResult.Success(recommendations)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch recommendations: ${e.message}")
            }
        }
    }

    override suspend fun getUnappliedRecommendations(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return withContext(Dispatchers.Default) {
            try {
                val recommendations = database.schemaQueries.selectUnappliedRecommendations(goalId)
                    .awaitAsList()
                    .map { it.toSavingsRecommendation() }
                DatabaseResult.Success(recommendations)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch unapplied recommendations: ${e.message}")
            }
        }
    }

    override suspend fun markRecommendationApplied(recommendationId: Long): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.markRecommendationApplied(recommendationId)
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to apply recommendation: ${e.message}")
            }
        }
    }

    override suspend fun deleteRecommendationsForGoal(goalId: String): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.deleteRecommendationsForGoal(goalId)
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to delete recommendations: ${e.message}")
            }
        }
    }

    override suspend fun recordContribution(
        goalId: String,
        userId: String,
        amount: Double,
        date: LocalDate,
        notes: String?
    ): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.insertProgressEntry(
                    goal_id = goalId,
                    user_id = userId,
                    contribution_amount = amount,
                    contribution_date = date.toString(),
                    notes = notes
                )
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to record contribution: ${e.message}")
            }
        }
    }

    override suspend fun getProgressHistory(goalId: String): DatabaseResult<List<GoalContribution>> {
        return withContext(Dispatchers.Default) {
            try {
                val history = database.schemaQueries.selectProgressHistoryByGoal(goalId)
                    .awaitAsList()
                    .map { it.toGoalContribution() }
                DatabaseResult.Success(history)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to fetch progress history: ${e.message}")
            }
        }
    }

    override suspend fun getTotalContributions(goalId: String): DatabaseResult<Double> {
        return withContext(Dispatchers.Default) {
            try {
                val total = database.schemaQueries.selectProgressHistoryByGoal(goalId)
                    .awaitAsList()
                    .sumOf { it.contribution_amount }
                DatabaseResult.Success(total)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to calculate total contributions: ${e.message}")
            }
        }
    }

    override suspend fun getMonthlyContributions(goalId: String): DatabaseResult<List<MonthlyContribution>> {
        return withContext(Dispatchers.Default) {
            try {
                val history = database.schemaQueries.selectProgressHistoryByGoal(goalId)
                    .awaitAsList()

                val monthlyMap = mutableMapOf<String, Pair<Double, Int>>()
                history.forEach { entry ->
                    val month = entry.contribution_date.substring(0, 7)
                    val (currentTotal, count) = monthlyMap[month] ?: Pair(0.0, 0)
                    monthlyMap[month] = Pair(currentTotal + entry.contribution_amount, count + 1)
                }

                val monthlyContributions = monthlyMap.map { (month, totalAndCount) ->
                    MonthlyContribution(
                        month = month,
                        totalAmount = totalAndCount.first,
                        contributionCount = totalAndCount.second
                    )
                }.sortedBy { it.month }

                DatabaseResult.Success(monthlyContributions)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to calculate monthly contributions: ${e.message}")
            }
        }
    }

    override suspend fun deleteProgressHistory(goalId: String): DatabaseResult<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                database.schemaQueries.deleteProgressHistoryForGoal(goalId)
                DatabaseResult.Success(Unit)
            } catch (e: Exception) {
                DatabaseResult.Error(e, "Failed to delete progress history: ${e.message}")
            }
        }
    }

    private suspend fun generateSampleRecommendations(goal: SavingsGoal, userId: String) {
        val sampleRecommendations = listOf(
            SavingsRecommendation(
                goalId = goal.id,
                spendingCategory = "Dining_Out",
                currentMonthlySpending = 350.0,
                recommendedMonthlySpending = 200.0,
                suggestedReduction = 150.0,
                rationale = "A modest 43% reduction in Dining Out will help you reach your ${goal.name} goal faster.",
                potentialSavings = 150.0,
                confidenceScore = 0.85,
                priority = 1
            ),
            SavingsRecommendation(
                goalId = goal.id,
                spendingCategory = "Entertainment",
                currentMonthlySpending = 200.0,
                recommendedMonthlySpending = 120.0,
                suggestedReduction = 80.0,
                rationale = "Cutting back on entertainment expenses by 40% could save you $80/month.",
                potentialSavings = 80.0,
                confidenceScore = 0.78,
                priority = 2
            ),
            SavingsRecommendation(
                goalId = goal.id,
                spendingCategory = "Shopping",
                currentMonthlySpending = 300.0,
                recommendedMonthlySpending = 180.0,
                suggestedReduction = 120.0,
                rationale = "Reducing shopping by 40% could accelerate your savings timeline significantly.",
                potentialSavings = 120.0,
                confidenceScore = 0.72,
                priority = 3
            ),
            SavingsRecommendation(
                goalId = goal.id,
                spendingCategory = "Subscriptions",
                currentMonthlySpending = 150.0,
                recommendedMonthlySpending = 80.0,
                suggestedReduction = 70.0,
                rationale = "Review and cancel unused subscriptions to save $70/month toward your goal.",
                potentialSavings = 70.0,
                confidenceScore = 0.90,
                priority = 4
            )
        )

        saveRecommendations(sampleRecommendations, userId)
    }
}

private fun com.wiz3r.database.Savings_goals.toSavingsGoal(): SavingsGoal {
    val localDate = LocalDate.parse(deadline)
    // Format as "Jan 15, 2026" for display
    val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val deadlineStr = "${monthNames[localDate.monthNumber - 1]} ${localDate.dayOfMonth}, ${localDate.year}"

    return SavingsGoal(
        id = id,
        name = name,
        description = description ?: "",
        targetAmount = target_amount,
        currentAmount = current_amount ?: 0.0,
        deadline = localDate,
        category = GoalCategory.valueOf(category),
        priority = GoalPriority.valueOf(priority ?: "MEDIUM"),
        createdDate = Instant.parse(created_date),
        isActive = is_active == 1L,
        iconEmoji = icon_emoji ?: "",
        deadlineStr = deadlineStr,
        // Wiz 3R AI fields — null until set via the goal edit UI / API.
        accountType = account_type?.let { runCatching { GoalAccountType.valueOf(it) }.getOrNull() },
        debtInterestRate = debt_interest_rate,
        debtRemainingBalance = debt_remaining_balance,
        debtRemainingMonths = debt_remaining_months?.toInt(),
        debtPaymentFrequency = debt_payment_frequency?.let { runCatching { PaymentFrequency.valueOf(it) }.getOrNull() },
        debtMonthlyEquivalentPayment = debt_monthly_equivalent_payment,
    )
}

/**
 * Parse SQL timestamp format 'YYYY-MM-DD HH:MM:SS' to Instant
 * SQLite CURRENT_TIMESTAMP returns this format, not ISO-8601
 */
private fun parseSqlTimestamp(timestamp: String): Instant {
    // Convert SQL timestamp format to ISO-8601 format
    // '2025-10-06 11:32:09' -> '2025-10-06T11:32:09Z'
    val isoFormat = timestamp.replace(" ", "T") + "Z"
    return Instant.parse(isoFormat)
}

private fun com.wiz3r.database.Savings_recommendations.toSavingsRecommendation() = SavingsRecommendation(
    goalId = goal_id,
    spendingCategory = spending_category,
    currentMonthlySpending = current_monthly_spending,
    recommendedMonthlySpending = recommended_monthly_spending,
    suggestedReduction = suggested_reduction,
    rationale = rationale,
    potentialSavings = potential_savings,
    confidenceScore = confidence_score,
    priority = priority.toInt()
)

private fun com.wiz3r.database.Goal_progress_history.toGoalContribution() = GoalContribution(
    id = id,
    goalId = goal_id,
    userId = user_id,
    amount = contribution_amount,
    date = LocalDate.parse(contribution_date),
    notes = notes,
    createdAt = Clock.System.now()
)
